/**
 * src/pages/Prospeccion.jsx
 * Prospección del universo de proveedores del CM: avance de la extracción
 * (F2 seller_codes → F3 actividad 90d → F4 scoring), último run y leads.
 *
 * Permite seguir el avance desde el navegador sin comandos: los contadores por
 * estado se refrescan solos mientras corre el job.
 */
import { useState } from 'react'
import { Header } from '../components/layout/Header'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Card, KpiCard } from '../components/ui/Card'
import { Table } from '../components/ui/Table'
import { descargarProspeccionCsv, descargarProspeccionXlsx } from '../api/prospeccion'
import { getSondaProveedor } from '../api/scraping'
import {
  useEjecutarProspeccion, useProspeccionLeads, useProspeccionStatus,
} from '../hooks/useProspeccion'

// Estados del ciclo de extracción, en orden de avance.
const ESTADOS = [
  { key: 'pendiente',            label: 'Pendiente',      color: 'gray',
    ayuda: 'Importado de F1; falta resolver su seller_code' },
  { key: 'seller_ok',            label: 'Seller OK',      color: 'blue',
    ayuda: 'Con seller_code; barriendo su actividad de 90 días' },
  { key: 'actividad_ok',         label: 'Actividad OK',   color: 'green',
    ayuda: 'Ventana de 90 días cubierta' },
  { key: 'seller_no_encontrado', label: 'Sin seller',     color: 'yellow',
    ayuda: 'BuscarProveedor no devolvió resultado; no se reintenta' },
  { key: 'error',                label: 'Error',          color: 'red',
    ayuda: 'Fallo persistente en la extracción' },
]

// Score A/B/C (PASO 4 del spec): A = target primario (intermedios).
const SCORE_COLOR = { A: 'green', B: 'blue', C: 'gray' }
const POSICION_COLOR = { 'Líder': 'purple', 'Medio': 'green', 'Cola': 'gray' }
const TENDENCIA_COLOR = { '↑': 'green', '→': 'gray', '↓': 'red' }

// Por qué se detuvo la corrida. Distinguirlo importa: 'throttling' no es un
// fallo — es prospección cediendo la cuota a los diagnósticos F1, que tienen
// prioridad — pero sí explica que se avance poco esa noche.
const MOTIVO = {
  completado:     { label: 'Sin trabajo pendiente', color: 'green' },
  cuota_agotada:  { label: 'Presupuesto agotado',   color: 'blue' },
  throttling:     { label: 'Cuota MP saturada',     color: 'yellow' },
  sin_ticket:     { label: 'Falta MP_API_TICKET',   color: 'red' },
}

function fmtFecha(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('es-CL', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  })
}

function fmtMonto(v) {
  if (v == null) return '—'
  return `$${Number(v).toLocaleString('es-CL')}`
}

const COLUMNS = [
  {
    key: 'razon_social', label: 'Proveedor',
    render: (v, row) => (
      <div>
        <div style={{ fontWeight: 500 }}>{v}</div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
          {row.rut}{row.seller_code ? ` · ${row.seller_code}` : ''}
        </div>
      </div>
    ),
  },
  {
    key: 'score', label: 'Score', align: 'center', width: '70px',
    render: (v) => (v ? <Badge color={SCORE_COLOR[v] || 'gray'}>{v}</Badge> : '—'),
  },
  {
    key: 'posicion', label: 'Posición', width: '90px',
    render: (v) => (v ? <Badge color={POSICION_COLOR[v] || 'gray'}>{v}</Badge> : '—'),
  },
  {
    key: 'tendencia', label: 'Tend.', align: 'center', width: '70px',
    render: (v) => (v ? <Badge color={TENDENCIA_COLOR[v] || 'gray'}>{v}</Badge> : '—'),
  },
  { key: 'n_ocs_90d', label: 'OCs 90d', align: 'right', mono: true, width: '80px' },
  {
    key: 'monto_90d', label: 'Monto 90d', align: 'right', mono: true, nowrap: true,
    render: (v) => fmtMonto(v),
  },
  { key: 'n_organismos', label: 'Organismos', align: 'right', mono: true, width: '100px' },
  {
    key: 'contacto_persona', label: 'Contacto',
    render: (v, row) => (
      <div>
        <div>{v || <span style={{ color: 'var(--color-text-3)' }}>—</span>}</div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)' }}>
          {row.email || row.telefono || ''}
        </div>
      </div>
    ),
  },
  {
    key: 'estado_contacto', label: 'CRM', width: '120px',
    render: (v) => <Badge color={v === 'Sin contactar' ? 'gray' : 'blue'}>{v || '—'}</Badge>,
  },
  {
    key: 'plan_sugerido', label: 'Plan', width: '90px',
    render: (v) => (v ? <Badge color={v === 'Premium' ? 'purple' : v === 'Pro' ? 'blue' : 'gray'}>{v}</Badge> : '—'),
  },
  {
    key: 'estado_extraccion', label: 'Extracción', width: '130px',
    render: (v) => {
      const e = ESTADOS.find(x => x.key === v)
      return <Badge color={e?.color || 'gray'} dot>{e?.label || v}</Badge>
    },
  },
  {
    key: 'ultimo_dia_procesado', label: 'Checkpoint', mono: true, nowrap: true, width: '110px',
    render: (v) => (v || '—'),
  },
]

function SondaProveedor() {
  const [seller, setSeller] = useState('')
  const [dias, setDias] = useState(30)
  const [res, setRes] = useState(null)
  const [loading, setLoading] = useState(false)

  async function run() {
    if (!seller.trim().match(/^\d+$/)) { setRes({ error: 'seller_code numérico' }); return }
    setLoading(true); setRes(null)
    try { setRes(await getSondaProveedor(seller.trim(), dias)) }
    catch (e) { setRes({ error: e.response?.data?.detail || 'No se pudo sondear (¿timeout? prueba menos días)' }) }
    finally { setLoading(false) }
  }

  return (
    <Card title="Sonda de proveedor (vetting pre-onboarding)"
          subtitle="¿Vende por catálogo CM o por Compra Ágil? Muestrea sus OCs sin crear tenant. Puede tardar ~30-60 s.">
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={seller} onChange={e => setSeller(e.target.value)} placeholder="seller_code (ej. 1830721)"
               style={{ padding: '8px 10px', fontSize: 'var(--text-base)', fontFamily: 'var(--font-mono)',
                        border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
                        background: 'var(--color-surface-2)', color: 'var(--color-text)', width: '200px' }} />
        <select value={dias} onChange={e => setDias(Number(e.target.value))}
                style={{ padding: '8px 10px', fontSize: 'var(--text-base)', border: '1px solid var(--color-border)',
                         borderRadius: 'var(--radius-sm)', background: 'var(--color-surface-2)', color: 'var(--color-text)' }}>
          {[15, 30, 45, 60, 90].map(d => <option key={d} value={d}>{d} días</option>)}
        </select>
        <Button size="sm" onClick={run} loading={loading}>Sondear</Button>
      </div>

      {res?.error && (
        <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)', color: 'var(--color-error)', fontFamily: 'var(--font-mono)' }}>✗ {res.error}</div>
      )}
      {res && !res.error && (
        <div style={{ marginTop: '14px', display: 'flex', gap: '28px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 'var(--text-xs)', letterSpacing: '0.08em', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>% CATÁLOGO CM</div>
            <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, fontFamily: 'var(--font-mono)',
                          color: res.apto_demo ? 'var(--color-ok)' : 'var(--color-error)' }}>
              {res.pct_catalogo_cm}%
            </div>
          </div>
          <div style={{ fontSize: 'var(--text-base)', fontFamily: 'var(--font-mono)', color: 'var(--color-text-2)' }}>
            <div>OCs muestreadas: <b style={{ color: 'var(--color-text)' }}>{res.ocs_muestreadas}</b> ({res.dias} días)</div>
            <div>Por tipo: {Object.entries(res.por_tipo || {}).map(([t, n]) => `${t}:${n}`).join('  ') || '—'}</div>
            <div>Familia: {Object.entries(res.familia_estimada || {}).map(([f, n]) => `${f}:${n}`).join('  ') || '—'}</div>
            <div>{res.apto_demo
              ? <Badge color="green">Apto: catálogo CM en familia soportada</Badge>
              : <Badge color="yellow">No apto (poco CM, o familia no soportada: aseo/otros)</Badge>}</div>
          </div>
        </div>
      )}
      {res && !res.error && (res.muestra_productos || []).length > 0 && (
        <div style={{ marginTop: '10px', fontSize: 'var(--text-xs)', fontFamily: 'var(--font-mono)',
                      color: 'var(--color-text-3)', lineHeight: 1.5 }}>
          <div style={{ marginBottom: 'var(--space-1)' }}>Muestra de productos vendidos (revela la familia real):</div>
          {(res.muestra_productos || []).slice(0, 12).map((p, i) => <div key={i}>· {p}</div>)}
        </div>
      )}
    </Card>
  )
}

export default function Prospeccion() {
  const [estado, setEstado] = useState('')
  const [descargando, setDescargando] = useState(false)
  const [aviso, setAviso] = useState(null)

  const { data: status, isLoading: cargandoStatus } = useProspeccionStatus()
  const { data: leads, isLoading: cargandoLeads } = useProspeccionLeads({ estado, limit: 100 })
  const ejecutar = useEjecutarProspeccion()

  // Sin `|| {}`: si la respuesta no trae el desglose, cada estado llega
  // ausente y la tarjeta lo dice, en vez de afirmar que hay cero leads en
  // cada casilla — que es lo que se veía cuando la petición fallaba.
  const porEstado = status?.por_estado
  const ultimo = status?.ultimo_run

  async function handleExport(formato) {
    setDescargando(formato)
    setAviso(null)
    try {
      await (formato === 'xlsx' ? descargarProspeccionXlsx() : descargarProspeccionCsv())
    } catch {
      setAviso({ tipo: 'error', texto: `No se pudo descargar el ${formato.toUpperCase()}.` })
    } finally {
      setDescargando(false)
    }
  }

  function handleEjecutar() {
    setAviso(null)
    ejecutar.mutate({ maxRequests: 8000, background: true }, {
      onSuccess: (r) => setAviso(r?.lanzado
        ? { tipo: 'ok', texto: 'Run lanzado en segundo plano. El avance se refresca solo.' }
        : { tipo: 'warn', texto: 'Ya hay una corrida en curso.' }),
      onError: () => setAviso({ tipo: 'error', texto: 'No se pudo lanzar el run.' }),
    })
  }

  return (
    <>
      <Header
        title="Prospección"
        subtitle="Universo de proveedores del Convenio Marco · job diario 03:00 (Chile)"
        action={
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button variant="secondary" size="sm" onClick={() => handleExport('xlsx')}
                    loading={descargando === 'xlsx'}>
              Excel (4 hojas)
            </Button>
            <Button variant="ghost" size="sm" onClick={() => handleExport('csv')}
                    loading={descargando === 'csv'}>
              CSV
            </Button>
            <Button size="sm" onClick={handleEjecutar} loading={ejecutar.isPending}>
              Ejecutar ahora
            </Button>
          </div>
        }
      />

      <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <SondaProveedor />
        {aviso && (
          <div style={{
            padding: '10px 14px', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
            fontFamily: 'var(--font-mono)',
            background: aviso.tipo === 'error' ? 'var(--color-error-soft)'
              : aviso.tipo === 'warn' ? 'var(--color-warn-soft)' : 'var(--color-ok-soft)',
            color: aviso.tipo === 'error' ? 'var(--color-error)'
              : aviso.tipo === 'warn' ? 'var(--color-warn)' : 'var(--color-ok)',
            border: '1px solid var(--color-border)',
          }}>
            {aviso.texto}
          </div>
        )}

        {/* Avance global */}
        <div style={{
          display: 'grid', gap: 'var(--space-3)',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        }}>
          <KpiCard label="TOTAL LEADS" valor={status?.total_leads} cargando={cargandoStatus}
                   nada="sin leads importados todavía" />
          {ESTADOS.map(e => (
            <KpiCard
              key={e.key}
              label={e.label.toUpperCase()}
              valor={porEstado?.[e.key]}
              cargando={cargandoStatus}
              sub={e.ayuda}
              color={
                e.color === 'green' ? 'var(--color-ok)'
                : e.color === 'blue' ? 'var(--color-running)'
                : e.color === 'yellow' ? 'var(--color-warn)'
                : e.color === 'red' ? 'var(--color-error)' : 'var(--color-text-2)'
              }
            />
          ))}
        </div>

        {/* Último run */}
        <Card title="Último run" subtitle="Resumen de la última corrida (F2 → F3 → F4)">
          {ultimo ? (
            <div style={{
              display: 'grid', gap: '18px',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              fontSize: 'var(--text-base)',
            }}>
              <div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>FECHA</div>
                <div style={{ marginTop: 'var(--space-1)' }}>{fmtFecha(ultimo.fecha)}</div>
              </div>
              <div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>REQUESTS USADOS</div>
                <div style={{ marginTop: 'var(--space-1)', fontFamily: 'var(--font-mono)' }}>{ultimo.requests_usados}</div>
              </div>
              <div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>COMPLETADOS</div>
                <div style={{ marginTop: 'var(--space-1)', fontFamily: 'var(--font-mono)' }}>{ultimo.proveedores_completados}</div>
              </div>
              <div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>ERRORES</div>
                <div style={{
                  marginTop: 'var(--space-1)', fontFamily: 'var(--font-mono)',
                  color: ultimo.errores > 0 ? 'var(--color-error)' : 'var(--color-text)',
                }}>
                  {ultimo.errores}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>POR QUÉ TERMINÓ</div>
                <div style={{ marginTop: 'var(--space-1)' }}>
                  <Badge color={MOTIVO[ultimo.detalle?.f3?.motivo]?.color || 'gray'}>
                    {MOTIVO[ultimo.detalle?.f3?.motivo]?.label || ultimo.detalle?.f3?.motivo || '—'}
                  </Badge>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
              Todavía no hay corridas registradas.
            </div>
          )}
        </Card>

        {/* Leads */}
        <Card
          title="Leads"
          subtitle="Score A (target primario) primero, y dentro de cada score por monto"
          padding="0"
          action={
            <select
              value={estado}
              onChange={e => setEstado(e.target.value)}
              style={{
                background: 'var(--color-surface-2)', color: 'var(--color-text)',
                border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
                padding: '5px 10px', fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)',
              }}
            >
              <option value="">Todos los estados</option>
              {ESTADOS.map(e => (
                <option key={e.key} value={e.key}>{e.label}</option>
              ))}
            </select>
          }
        >
          <Table
            columns={COLUMNS}
            data={leads}
            loading={cargandoLeads}
            emptyMessage="Sin leads. Importa el JSON de F1 para poblar el universo."
          />
        </Card>
      </div>
    </>
  )
}
